import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

/* global __BUILD_ID__ */

const buildId = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : Date.now().toString();

const pathDetector = {
    name: 'pathDetector',
    lookup() {
        if (typeof window !== 'undefined') {
            if (window.location.pathname.startsWith('/de')) {
                return 'de';
            }
            return 'en';
        }
        return 'en';
    },
};

const customLanguageDetector = new LanguageDetector();
customLanguageDetector.addDetector(pathDetector);

async function configureI18n() {
    let backendToUse;
    if (import.meta.env.SSR) {
        try {
            const FsBackend = (await import('i18next-fs-backend')).default;
            backendToUse = FsBackend;
        } catch (e) {
            console.error('[i18n] Failed to load FsBackend for SSR, falling back to HttpBackend.', e);
            backendToUse = HttpBackend;
        }
    } else {
        backendToUse = HttpBackend;
    }

    await i18n
        .use(backendToUse)
        .use(customLanguageDetector)
        .use(initReactI18next)
        .init({
            debug: import.meta.env.DEV,
            supportedLngs: ['de', 'en'],
            fallbackLng: 'en',
            ns: ['Common', 'Homepage'],
            defaultNS: 'Common',
            load: 'currentOnly',
            interpolation: {
                escapeValue: false,
            },
            react: {
                useSuspense: true,
            },
            detection: {
                order: ['pathDetector', 'localStorage', 'navigator'],
                lookupLocalStorage: 'app-language',
                caches: ['localStorage'],
            },
            backend: {
                loadPath: (lngs, namespaces) => {
                    let filePath;
                    let actualNamespace;

                    if (Array.isArray(namespaces) && namespaces.length > 0) {
                        actualNamespace = namespaces[0];
                    } else if (typeof namespaces === 'string' && namespaces.length > 0) {
                        actualNamespace = namespaces;
                    } else {
                        const defaultNS =
                            i18n.options && i18n.options.defaultNS && typeof i18n.options.defaultNS === 'string' ? i18n.options.defaultNS : 'Common';
                        actualNamespace = defaultNS;
                    }

                    if (import.meta.env.SSR) {
                        let langToLoad;
                        if (Array.isArray(lngs) && lngs.length > 0) {
                            langToLoad = lngs[0];
                        } else if (typeof lngs === 'string') {
                            langToLoad = lngs;
                        }

                        const resolvedLanguage = langToLoad || (i18n.options && i18n.options.fallbackLng && i18n.options.fallbackLng[0]) || 'en';

                        if (actualNamespace === 'Common') {
                            filePath = `public/build/resources/lang/${resolvedLanguage}.json`;
                        } else if (actualNamespace) {
                            filePath = `public/build/modules/${actualNamespace}/Resources/lang/${resolvedLanguage}.json`;
                        } else {
                            filePath = `public/build/resources/lang/${resolvedLanguage}.json`;
                        }
                    } else {
                        const clientLanguage = lngs[0];
                        if (actualNamespace === 'Common') {
                            filePath = `/build/resources/lang/${clientLanguage}.json?v=${buildId}`;
                        } else if (actualNamespace) {
                            filePath = `/build/modules/${actualNamespace}/Resources/lang/${clientLanguage}.json?v=${buildId}`;
                        } else {
                            filePath = `/build/resources/lang/${clientLanguage}.json?v=${buildId}`;
                        }
                    }
                    return filePath;
                },
            },
            partialBundledLanguages: true,
        });

    return i18n;
}

const i18nPromise = configureI18n();
export default i18nPromise;
